import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonButtonRowWidget, UtilityService } from '@eforall/common';
import { DialogService, EmailLink, LinkButtonWidget } from '@eforall/dashboard';
import { LanguageId } from '@interfaces';
import { environment } from '../../../../../../environments/environment';
import { AppService, FuncService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../accelerator-area.service';
import { CohortMeetCodeEditorDialog } from './cohort-meet-code-editor/cohort-meet-code-editor.dialog';
import { FacilitatorScheduleLinkEditorDialog } from './facilitator-schedule-link-editor/facilitator-schedule-link-editor.dialog';


@Component({
	selector: 'acc-cohort-tab-1-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
		LinkButtonWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './acc-cohort-tab-1.part.html',
})
export class AccCohortTab1Part {

	private readonly app = inject(AppService);
	private readonly func = inject(FuncService);
	private readonly util = inject(UtilityService);
	private readonly dialogService = inject(DialogService);
	private readonly areaService = inject(AcceleratorAreaService);

	public acc = computed(() => this.areaService.data()?.acc);

	public cohortMeetButtonText = computed(() =>
		this.acc()?.googleMeetCode ? 'Edit Meet Code' : 'Add Meet Code'
	);

	public officeHoursButtonText = computed(() =>
		this.acc()?.facilitator.scheduleLink ? 'Edit Link' : 'Add Link'
	);

	// check if any event is missing a Google Calendar ID
	public isAnyEventMissingCalendarId = computed(() => this.areaService.data()?.events.some(e => !e.googleCalendarEventId));

	public anyEventsWithCalendarId = computed(() => (this.areaService.data()?.events.filter(e => !!e.googleCalendarEventId) || []).length > 0);

	async generateClassEvents() {

		const data = this.areaService.data();
		if (!data) return;

		// Restrict generating Google Calendar events in non-PROD environments unless the user has softwareDev permission.
		if (environment.name !== 'PROD') {
			let proceed = false;
			if (this.app.data().permissions.isSoftwareDev) {
				proceed = await this.dialogService.confirm('WARNING - THIS CANNOT BE UNDONE: This will generate live Google Calendar events with the current data in DEV. This should not be run unless the program has been configured with fake data or staff members for testing purposes. PROCEED WITH CAUTION. Do you want to continue?');
			} else {
				await this.dialogService.showMessage('Restricted in Non-PROD environments', 'You do not have permission to generate Google Calendar events in DEV. Please contact tech@eforall.org for more details.', 'Okay', [], 300, 300);
			}
			if (!proceed) return;
		}

		if (data.acc.googleMeetCode && !data.acc.isMeetCodeGenerated) {
			const yes = await this.dialogService.confirm('A Google Meet code was already added manually. Please confirm if you want to OVERRIDE this code and allow the app to generate a NEW Meet code along with the corresponding Calendar events.', 'Yes', 'No', ['Note: If you have already shared the existing Meet code externally, you will need to redistribute the new code.', 'WARNING: This should only be done before the Kick-off Class!']);
			if (!yes) return;
		} else {
			const yes = await this.dialogService.confirm('Generate a Google Meet via Google Calendar. This will automatically create a list of calendar events for the cohort, generate a Google Meet, and invite all Entrepreneurs.', 'Yes', 'No', ['IMPORTANT: If you need to change classes before sending invites, please update them before running this.']);
			if (!yes) return;
		}

		await this.func.googleCalendar.acc.createClassCalendarEvents({ accId: data.acc.accId });
		await this.areaService.reloadData();
	}

	async openCohortMeetCodeEditor() {
		const acc = this.acc();
		if (!acc) return;

		await this.dialogService.showCustom(CohortMeetCodeEditorDialog, { data: { accId: acc.accId, meetCode: acc.googleMeetCode, isMeetCodeGenerated: !!acc.isMeetCodeGenerated } }, 500, 400);
		await this.areaService.reloadData();
	}

	async openFacilitatorScheduleLinkEditor() {
		const acc = this.acc();
		if (!acc) return;

		await this.dialogService.showCustom(FacilitatorScheduleLinkEditorDialog, { data: { accId: acc.accId, scheduleLink: acc.facilitator.scheduleLink } }, 500, 250);
		await this.areaService.reloadData();
	}

	async updateClassAttendees() {
		const data = this.areaService.data();
		if (!data) return;

		// Restrict updating Google Calendar attendees in non-PROD environments unless the user has softwareDev permission.
		if (environment.name !== 'PROD') {
			let proceed = false;
			if (this.app.data().permissions.isSoftwareDev) {
				proceed = await this.dialogService.confirm('WARNING - THIS CANNOT BE UNDONE: This will update live Google Calendar attendees with the current data in DEV. This should not be run unless the program has been configured with fake data or staff members for testing purposes. PROCEED WITH CAUTION. Do you want to continue?');
			} else {
				await this.dialogService.showMessage('Restricted in Non-PROD environments', 'You do not have permission to update Google Calendar attendees in DEV. Please contact tech@eforall.org for more details.', 'Okay', [], 300, 300);
			}
			if (!proceed) return;
		}

		await this.func.googleCalendar.acc.refreshClassesCalendarEventAttendees({ accId: data.acc.accId });
	}

	openGoogleMeet(code: string) {
		const url = this.util.url.getGoogleMeetLink(code);
		if (url)
			this.openURL(url);
	}

	openURL(url: string) {
		window.open(url, '_blank');
	}

	async createGoogleGroups(accId: number) {

		// Restrict creating Google Groups in non-PROD environments unless the user has softwareDev permission.
		if (environment.name !== 'PROD') {
			let proceed = false;
			if (this.app.data().permissions.isSoftwareDev) {
				proceed = await this.dialogService.confirm('WARNING - THIS CANNOT BE UNDONE: This will create live Google Groups with the current data in DEV. This should not be run unless the program has been configured with fake data or staff members for testing purposes. PROCEED WITH CAUTION. Do you want to continue?');
			} else {
				await this.dialogService.showMessage('Restricted in Non-PROD environments', 'You do not have permission to create Google Groups in DEV. Please contact tech@eforall.org for more details.', 'Okay', [], 300, 300);
			}
			if (!proceed) return;
		}

		await this.func.googleGroups.createGroupsForAcc({ accId });
		await this.areaService.reloadData();
	}

	getGroupLink(group: string, languageId: number): EmailLink {
		const domain = languageId === LanguageId.English ? 'eforall.org' : 'eparatodos.org';
		return {
			linkType: 'email',
			email: `${group}@${domain}`,
		};
	}
}
